/**
 * Matching P - S.
 *
 * Mirror Access form `frmMatchingPSMain` (file frontend/plan/access_all/form_frmMatchingPSMain.txt):
 *   Caption = "MATCHING P - S", NavigationControl0 — 4 nav button visible:
 *     Match - Instruct        (frmMatchingPSMainSubForm)
 *     Allocating: Stock-Sales (frmMatchingPSMatch_Stock_Sales)
 *     S Contract              (frmMatchingPSMatchLocalSQry)
 *     P Contract              (frmMatchingPSMatchLocalPQry)
 *   (navProcurementPreferences "References" Visible=NotDefault → ẩn; "[Add New]" là design placeholder).
 *
 * URL routing: `/matching/:tab` — defaults to `match-instruct`.
 */
import { Box, Card, CardContent, CircularProgress, Tab, Tabs } from '@mui/material'
import { useEffect, useMemo } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import ReloadButton from '@/components/ReloadButton'
import { usePreference } from '@/hooks/usePreference'

import MatchInstructTab from './contractOverview/MatchInstructTab'
import PContractTab from './contractOverview/PContractTab'
import SContractTab from './contractOverview/SContractTab'
import StockSalesTab from './contractOverview/StockSalesTab'
import {
  DEFAULT_CONTRACT_OVERVIEW_TAB,
  VISIBLE_CONTRACT_OVERVIEW_TABS,
  isVisibleContractOverviewTabKey,
  type ContractOverviewTabKey,
} from './contractOverview/contractOverviewTabs'

// Format nút header đồng bộ các page đã có (Futures Control / Report / Sale Control).
const headerButtonSx = {
  minWidth: 0, // cho phép button icon-only compact (MUI default 64px)
  minHeight: 24,
  px: { xs: 0.5, sm: 0.75 },
  py: 0.15,
  fontSize: '0.67rem',
  lineHeight: 1.1,
  textTransform: 'none' as const,
  '& .MuiButton-startIcon': {
    mr: { xs: 0, sm: 0.5 },
    ml: 0,
  },
}

function renderTab(key: ContractOverviewTabKey) {
  switch (key) {
    case 'match-instruct':
      return <MatchInstructTab />
    case 'stock-sales':
      return <StockSalesTab />
    case 's-contract':
      return <SContractTab />
    case 'p-contract':
      return <PContractTab />
    default:
      return <MatchInstructTab />
  }
}

export default function MatchingContractOverviewPage() {
  const params = useParams<{ tab?: string }>()
  const location = useLocation()
  const navigate = useNavigate()

  // Per-user "last opened tab" — generic preferences registry (mirror Factory).
  const navPref = usePreference<{ lastTab: string }>('matching-ps', 'navigation', undefined, { refetchOnMount: true })

  const activeKey: ContractOverviewTabKey = useMemo(() => {
    if (isVisibleContractOverviewTabKey(params.tab)) return params.tab
    return DEFAULT_CONTRACT_OVERVIEW_TAB
  }, [params.tab])

  // Truy cập /matching (không :tab) HOẶC tab ẩn (vd /matching/stock-sales) → restore tab
  // cuối user đã mở (nếu navPref đã fetch xong + tab còn hợp lệ), fallback tab mặc định.
  useEffect(() => {
    if (!location.pathname.startsWith('/matching')) return
    if (isVisibleContractOverviewTabKey(params.tab)) return
    if (navPref.isLoading || navPref.isFetching) return
    const saved = navPref.value?.lastTab
    const target =
      saved && isVisibleContractOverviewTabKey(saved) ? saved : DEFAULT_CONTRACT_OVERVIEW_TAB
    navigate(`/matching/${target}`, { replace: true })
  }, [location.pathname, navigate, params.tab, navPref.isLoading, navPref.isFetching, navPref.value])

  // Auto-save tab đang mở (debounce 400ms) để lần sau vào /matching restore đúng.
  useEffect(() => {
    if (navPref.isLoading || navPref.isFetching) return
    // CHỉ lưu tab THẬT trên URL (:tab param); route gốc /matching → KHÔNG lưu.
    if (!isVisibleContractOverviewTabKey(params.tab)) return
    if (navPref.value?.lastTab === params.tab) return
    void navPref.patch({ lastTab: params.tab }).catch(() => undefined)
  }, [params.tab, navPref.value?.lastTab, navPref.patch])

  // Resolve-first: URL chưa có :tab hợp lệ (đang đọc DB + chờ restore) → hiện
  // loading, KHÔNG render Page default. Có Page đúng trên URL mới render.
  if (!isVisibleContractOverviewTabKey(params.tab))
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={24} />
      </Box>
    )

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Hàng sub-nav (Tabs) + nút Reload chung cho TOÀN function (căn phải).
            ReloadButton invalidate tag `MatchingPs` → refetch tab đang mở. */}
        <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          // Tab ẩn (vd stock-sales) truy cập trực tiếp qua URL → không có <Tab> tương ứng,
          // dùng `false` để MUI không cảnh báo "invalid value".
          value={VISIBLE_CONTRACT_OVERVIEW_TABS.some((t) => t.key === activeKey) ? activeKey : false}
          onChange={(_, value: ContractOverviewTabKey) => {
            navigate(`/matching/${value}`, { replace: false })
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: { xs: 30, sm: 34 },
            '& .MuiTabs-indicator': {
              height: 2.5,
              borderRadius: '3px 3px 0 0',
            },
            '& .MuiTab-root': {
              position: 'relative',
              minHeight: { xs: 30, sm: 34 },
              minWidth: 0,
              py: 0.25,
              px: { xs: 1, sm: 1.5 },
              fontSize: { xs: '0.72rem', sm: '0.78rem' },
              fontWeight: 600,
              letterSpacing: 0.15,
              textTransform: 'none',
              color: 'text.secondary',
              borderRadius: '6px 6px 0 0',
              transition: 'color 140ms ease, background-color 140ms ease',
              '&:hover': {
                color: 'text.primary',
                bgcolor: 'action.hover',
              },
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 700,
              },
              '&:not(:last-of-type)::after': {
                content: '""',
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                height: 16,
                width: '1px',
                bgcolor: 'divider',
                opacity: 0.6,
                transition: 'opacity 140ms ease',
              },
              '&:hover::after, &.Mui-selected::after': { opacity: 0 },
            },
          }}
        >
          {VISIBLE_CONTRACT_OVERVIEW_TABS.map((tab) => (
            <Tab key={tab.key} label={tab.label} value={tab.key} />
          ))}
        </Tabs>
          <ReloadButton
            sx={{ ...headerButtonSx, mx: 1, flexShrink: 0 }}
            apiTags={['MatchingPs']}
            baseTags={[]}
            watchEndpoints={['getMatchPOverview', 'getMatchSOverview', 'getMatchingPsSContract', 'getMatchingPsPContract']}
          />
        </Box>
        <CardContent
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            p: 0,
            '&:last-child': { pb: 0 },
          }}
        >
          {renderTab(activeKey)}
        </CardContent>
      </Card>
    </Box>
  )
}
